import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStatus, Me } from '../models/auth.model';

/**
 * Authentication state of the app. GET /api/v1/me is the only source of the signed in user, the
 * organization and the role. Nothing is kept in localStorage: the session lives in an HttpOnly cookie
 * that JavaScript cannot read, and the tokens never reach the browser code.
 *
 * With `environment.requireAuth` off (prototype mode) this service is inactive.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  /** False in prototype mode: no login, mock data. */
  readonly enabled = environment.requireAuth;

  readonly status = signal<AuthStatus>('unknown');
  readonly me = signal<Me | null>(null);
  /** API error code of the last failed check, e.g. USER_NOT_PROVISIONED. */
  readonly errorCode = signal<string | null>(null);
  readonly isAuthenticated = computed(() => this.status() === 'authenticated');

  private inflight: Promise<void> | null = null;

  /** Loads the identity from the server. Concurrent calls share one request. */
  load(force = false): Promise<void> {
    if (!force && this.status() !== 'unknown') return Promise.resolve();
    this.inflight ??= this.fetchMe().finally(() => (this.inflight = null));
    return this.inflight;
  }

  private async fetchMe(): Promise<void> {
    try {
      const me = await firstValueFrom(this.http.get<Me>('/api/v1/me'));
      this.me.set(me);
      this.errorCode.set(null);
      this.status.set('authenticated');
    } catch (error) {
      this.me.set(null);
      const code = errorCodeOf(error);
      this.errorCode.set(code);
      if (error instanceof HttpErrorResponse && error.status === 401) this.status.set('unauthenticated');
      else if (error instanceof HttpErrorResponse && error.status === 403 && (code === 'USER_NOT_PROVISIONED' || code === 'USER_DISABLED')) {
        this.status.set('not-provisioned');
      } else this.status.set('error');
    }
  }

  /** Starts the sign in. The server redirects to the identity provider and back to `returnTo`. */
  login(returnTo?: string): void {
    const target = returnTo ?? window.location.pathname + window.location.search;
    window.location.assign('/api/v1/auth/login?returnTo=' + encodeURIComponent(target));
  }

  /** Ends the local session and, if the server provides it, the identity provider session. */
  async logout(): Promise<void> {
    let logoutUrl: string | null = null;
    try {
      const result = await firstValueFrom(this.http.post<{ logoutUrl: string | null }>('/api/v1/auth/logout', {}));
      logoutUrl = result.logoutUrl;
    } catch {
      // The browser is sent away anyway. A stale cookie is rejected by the server.
    }
    this.markUnauthenticated();
    window.location.assign(logoutUrl ?? '/welcome');
  }

  markUnauthenticated(): void {
    this.me.set(null);
    this.status.set('unauthenticated');
  }

  markNotProvisioned(code: string): void {
    this.me.set(null);
    this.errorCode.set(code);
    this.status.set('not-provisioned');
  }
}

function errorCodeOf(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) return null;
  const body = error.error as { error?: { code?: string } } | null;
  return body?.error?.code ?? null;
}
